/**
 * ParentMessage Component
 *
 * Purpose: Displays a referenced parent message in a reply thread with navigation capability
 *
 * How it works:
 * 1. Fetches the parent message data using the provided ID
 * 2. Renders a compact preview of the original message
 * 3. When clicked, navigates to and highlights the original message in the chat
 * 4. Handles cases where the message might not be in the current view
 *
 * Key features:
 * - Message content preview with sender information
 * - Attachment type indication
 * - Click-to-navigate functionality with visual highlight
 * - Fallback loading state
 */

import { getMessageByServerIdFromLocalDB } from "@/indexedDB/services/messages";
import { toPlainText } from "@/lib/markdown";
import ParentMessageAttachment from "./attachments/ParentMessageAttachment";
import { cn } from "@/lib/utils";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import { useTranslation } from "react-i18next";
import { useMessageNavigation } from "./hooks/useMessageNavigation";

interface ParentMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  /** ID of the parent message to display */
  id: string;
  /** ID of the chat containing the message */
  chatId: string;
}

/**
 * ParentMessage component displays a referenced parent message in a reply thread
 */
export default function ParentMessage({
  id,
  chatId,
  className,
}: ParentMessageProps) {
  const message = useSafeLiveQuery(
    () => getMessageByServerIdFromLocalDB(id),
    [id],
  );
  const { navigateToMessage } = useMessageNavigation(chatId);
  const { t } = useTranslation();

  // Display loading state if message data isn't available yet
  if (!message) {
    return (
      <div className="flex h-14 min-h-14 min-w-40 shrink-0 animate-pulse cursor-pointer items-center gap-2 self-center rounded-sm border-l-4 border-background/70 bg-background/30 px-2 align-middle shadow-sm">
        <span className="opacity-50">{t("loading")}</span>
      </div>
    );
  }

  // Extract plain text content from rich text
  const plainTextContent = toPlainText(message.content.text);
  const hasTextContent = !!plainTextContent;
  const hasAttachment = message.hasAttachment;

  // Determine attachment type for display
  const getAttachmentTypeLabel = () => {
    const { content } = message;
    if (content.media?.fileType.startsWith("video/")) {
      return t("attachment.video");
    }
    if (content.media?.fileType.startsWith("image/")) {
      return t("attachment.image");
    }
    if (content.voice) {
      return t("attachment.voice");
    }
    if (content.file) {
      return t("attachment.file");
    }
    return "";
  };

  return (
    <div
      onClick={() => navigateToMessage(id)}
      className={cn(
        "flex h-14 min-h-14 min-w-40 cursor-pointer items-center gap-2 self-center rounded-sm px-2 text-left align-middle shadow-sm",
        className,
      )}
    >
      {hasAttachment && (
        <ParentMessageAttachment
          messageId={id}
          messageTimestamp={message.timestamp}
          chatId={chatId}
          messageContent={message.content}
        />
      )}

      <div className="flex w-full flex-col gap-0 overflow-hidden font-normal leading-tight">
        <div className="text-sm">{message.sender}</div>
        <p className="truncate text-sm text-foreground/90">
          {hasTextContent ? plainTextContent : getAttachmentTypeLabel()}
        </p>
      </div>
    </div>
  );
}
